import { IonContent, IonPage, IonSpinner } from "@ionic/react";
import { isEmbroideryTrace } from "../../config/appVariant";
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Converter from "../../components/Converter";
import { processAndAnalyzeImage } from "../../components/Converter/components/AutoUploadImage";
import AppContext from "../../contexts/AppContext";
import { useProcessedImage } from "../../contexts/ProcessedImageContext";
import PaywallModal from "../../components/PaywallModal/PaywallModal";
import {
  getPendingFileIdFromURL,
  loadPendingFileForConverter,
  storePendingFileId,
  claimPendingFile,
  clearPendingFile,
  downloadPendingFileAsBase64,
  OAUTH_STATE_EXPIRY_MS,
} from "../../lib/firebase/pendingFiles";
import { analyzeImageForRetrace } from "../../lib/firebase/imageAnalysis";
import { useAuth } from "../../contexts/AuthContext";
import { hideIntercom, showIntercom } from "../../lib/intercom/intercom";
import "./GuestConverter.css";

// Session storage key for persisting converter state across sign-in
const GUEST_CONVERTER_STATE_KEY = "guestConverterState";

/**
 * Save converter state to sessionStorage
 */
const saveConverterState = (state) => {
  try {
    sessionStorage.setItem(GUEST_CONVERTER_STATE_KEY, JSON.stringify({
      ...state,
      savedAt: Date.now(),
    }));
  } catch (e) {
    console.warn("Failed to save converter state:", e);
  }
};

/**
 * Load converter state from sessionStorage
 * Uses extended timeout for OAuth redirects which can take longer
 */
const loadConverterState = () => {
  try {
    const saved = sessionStorage.getItem(GUEST_CONVERTER_STATE_KEY);
    if (!saved) return null;
    
    const state = JSON.parse(saved);
    
    // Check if state is recent (30 minutes for OAuth redirects)
    // OAuth flows can involve email verification, 2FA, etc. which take time
    if (Date.now() - state.savedAt > OAUTH_STATE_EXPIRY_MS) {
      sessionStorage.removeItem(GUEST_CONVERTER_STATE_KEY);
      return null;
    }
    
    return state;
  } catch (e) {
    console.warn("Failed to load converter state:", e);
    return null;
  }
};

/**
 * Clear converter state from sessionStorage
 */
const clearConverterState = () => {
  sessionStorage.removeItem(GUEST_CONVERTER_STATE_KEY);
};

/**
 * GuestConverter Page
 * 
 * Publicly accessible converter for users coming from the marketing site.
 * Allows users to see their image converted without logging in.
 * Triggers paywall when they try to download/save.
 */
const GuestConverter = () => {
  const history = useHistory();
  const location = useLocation();
  const { user, userIsPremium } = useAuth();
  const { setGuestProcessedImage, guestProcessedImage, clearGuestProcessedImage } = useProcessedImage();
  
  // Hide Intercom chat widget on guest converter page
  useEffect(() => {
    hideIntercom();
    return () => showIntercom();
  }, []);

  // Ref to prevent multiple loads
  const hasLoadedRef = useRef(false);

  // Get the current file ID from URL
  const currentUrlFileId = useMemo(() => getPendingFileIdFromURL(), [location.search]);

  // Check if embroidery mode was requested via URL param
  const isEmbroideryMode = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("mode") === "embroidery";
  }, [location.search]);

  // Try to restore state from sessionStorage (for OAuth sign-in recovery)
  // BUT only if the saved state matches the current URL file ID
  const savedState = useMemo(() => {
    const loaded = loadConverterState();
    // If we have saved state but it's for a DIFFERENT file, clear it
    if (loaded && currentUrlFileId && loaded.pendingFileId !== currentUrlFileId) {
      console.log("🔄 URL file ID changed, clearing old saved state", {
        savedFileId: loaded.pendingFileId,
        currentUrlFileId,
      });
      clearConverterState();
      return null;
    }
    return loaded;
  }, [currentUrlFileId]);

  // Loading states
  const [isLoading, setIsLoading] = useState(!savedState);
  const [loadingMessage, setLoadingMessage] = useState("Loading your image...");
  const [error, setError] = useState(null);

  // Pending file data
  const [pendingFileId, setPendingFileId] = useState(savedState?.pendingFileId || null);
  const [pendingFileData, setPendingFileData] = useState(savedState?.pendingFileData || null);

  // Converter state - restore from saved state if available
  const [svgStore, setSvgStore] = useState();
  const [converterOpen, setConverterOpen] = useState(!!savedState);
  const [image64, setImage64] = useState(savedState?.image64);
  const [imagePrompt, setImagePrompt] = useState();
  const [imageSource, setImageSource] = useState("uploaded");
  const [imageSEO, setImageSEO] = useState();
  const [fileName, setFileName] = useState(savedState?.fileName);
  const [loadingConverter, setLoadingConverter] = useState(false);

  // Paywall modal
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallAction, setPaywallAction] = useState(null); // 'download' | 'save' | 'editor'
  const [pendingSvgData, setPendingSvgData] = useState(null);

  // AI Analysis result - determines if image needs retrace
  const [aiAnalysisResult, setAiAnalysisResult] = useState(null);

  // Track the last loaded file ID to detect changes
  const lastLoadedFileIdRef = useRef(null);

  // Reset state when URL file ID changes (user uploaded a new file)
  useEffect(() => {
    // If we've loaded a file before and the URL now has a different file ID
    if (lastLoadedFileIdRef.current && currentUrlFileId && 
        lastLoadedFileIdRef.current !== currentUrlFileId) {
      console.log("🔄 New file detected, resetting converter state", {
        previousFileId: lastLoadedFileIdRef.current,
        newFileId: currentUrlFileId,
      });
      
      // Reset all state for the new file
      hasLoadedRef.current = false;
      setPendingFileId(null);
      setPendingFileData(null);
      setImage64(undefined);
      setFileName(undefined);
      setConverterOpen(false);
      setIsLoading(true);
      setError(null);
      clearGuestProcessedImage();
      clearConverterState();
    }
  }, [currentUrlFileId, clearGuestProcessedImage]);

  // Restore processed image from saved state
  useEffect(() => {
    if (savedState?.processedImage) {
      console.log("🔄 Restoring processed image from saved state");
      setGuestProcessedImage(savedState.processedImage);
      // Track that we've loaded this file ID from saved state
      if (savedState.pendingFileId) {
        lastLoadedFileIdRef.current = savedState.pendingFileId;
        hasLoadedRef.current = true;
      }
    }
  }, [savedState, setGuestProcessedImage]);

  // Check if user is authenticated and premium
  const isAuthenticated = !!user;
  const isPremium = !!userIsPremium;

  // Check for OAuth redirect completion and restore paywall state if needed
  useEffect(() => {
    const oauthPending = sessionStorage.getItem("paywallOAuthPending");
    const oauthRedirectComplete = sessionStorage.getItem("oauthRedirectComplete");
    
    if (oauthRedirectComplete && oauthPending && user) {
      console.log("🔐 GuestConverter: OAuth redirect completed with user, restoring state");
      try {
        const pendingState = JSON.parse(oauthPending);
        console.log("🔐 GuestConverter: Restored paywall state:", pendingState);
        
        // Clean up
        sessionStorage.removeItem("paywallOAuthPending");
        sessionStorage.removeItem("oauthRedirectComplete");
        
        // If user is now premium, they can proceed with their action
        if (userIsPremium) {
          console.log("✅ GuestConverter: User is premium after OAuth, can proceed");
          // Don't show paywall - user can now download
        } else {
          console.log("🔐 GuestConverter: User not premium, will show subscription modal");
          // Show paywall with subscription options
          setPaywallAction(pendingState.action || "download");
          setShowPaywall(true);
        }
      } catch (e) {
        console.warn("Failed to parse OAuth pending state:", e);
        sessionStorage.removeItem("paywallOAuthPending");
        sessionStorage.removeItem("oauthRedirectComplete");
      }
    }
  }, [user, userIsPremium]);

  /**
   * Handle paywall trigger when user tries to download/save
   */
  const handlePaywallTrigger = useCallback((action, svgData = null) => {
    console.log("🔒 Paywall triggered:", action);
    setPaywallAction(action);
    setPendingSvgData(svgData);
    setShowPaywall(true);
  }, []);

  /**
   * Handle successful payment/signup
   */
  const handlePaywallSuccess = useCallback(async () => {
    console.log("✅ Paywall success! User completed signup/payment");
    setShowPaywall(false);

    // If user is now authenticated, claim the pending file
    if (pendingFileId && pendingFileData) {
      try {
        const claimResult = await claimPendingFile(pendingFileId);
        console.log("✅ Pending file claimed:", claimResult);
        
        // Store the claimed file data for potential later use
        const claimedFileInfo = {
          filePath: claimResult.filePath,
          previewUrl: claimResult.previewUrl || pendingFileData.previewUrl,
          imageData: pendingFileData.imageData,
          fileName: pendingFileData.fileName,
          processedImage: guestProcessedImage,
          pendingSvgData: pendingSvgData,
          action: paywallAction,
          embroideryMode: isEmbroideryMode,
          claimedAt: new Date().toISOString(),
        };
        
        sessionStorage.setItem("claimedFile", JSON.stringify(claimedFileInfo));
        console.log("✅ Claimed file info stored");
        
        clearPendingFile();
      } catch (error) {
        console.error("Failed to claim pending file:", error);
      }
    }

    // Clear the saved converter state since user completed sign-in
    clearConverterState();

    // Don't redirect - user stays on the page and can now download
    // The auth state will update and guestMode will become false
    console.log("✅ User is now signed in - they can click Download SVG again");
  }, [pendingFileId, pendingFileData, pendingSvgData, paywallAction, guestProcessedImage]);

  /**
   * Handle paywall close without completing
   */
  const handlePaywallClose = useCallback(() => {
    setShowPaywall(false);
    setPaywallAction(null);
    setPendingSvgData(null);
  }, []);

  /**
   * Load pending file on mount or when user auth state changes (e.g., after OAuth redirect)
   */
  useEffect(() => {
    const loadPendingFile = async () => {
      // Skip if already loaded (use ref to prevent race conditions)
      if (hasLoadedRef.current) {
        console.log("📁 File already loaded, skipping reload");
        return;
      }

      // Skip if we already have the converter open with file data (restored from session or already loaded)
      // This prevents re-loading on email/password sign-in and after OAuth redirect
      if (converterOpen && (pendingFileData || savedState)) {
        console.log("📁 File already loaded or restored, skipping reload");
        hasLoadedRef.current = true;
        return;
      }

      try {
        // Get file ID from URL
        const fileId = getPendingFileIdFromURL();

        if (!fileId) {
          setError("No file specified. Please upload an image from the homepage.");
          setIsLoading(false);
          return;
        }

        // Mark as loading to prevent duplicate loads
        hasLoadedRef.current = true;

        setPendingFileId(fileId);
        setLoadingMessage("Loading your image...");

        // Store the file ID in localStorage for retrieval after signup
        storePendingFileId(fileId);

        let fileData;
        let imageData;

        // If user is authenticated, try to claim first (handles already-claimed case)
        if (user) {
          console.log("🔑 User is authenticated, attempting to claim file...");
          try {
            const claimResult = await claimPendingFile(fileId);
            console.log("✅ Claim result:", claimResult);
            
            // Download the image using the preview URL from claim result
            if (claimResult.previewUrl) {
              imageData = await downloadPendingFileAsBase64(claimResult.previewUrl);
              fileData = {
                fileId,
                imageData,
                previewUrl: claimResult.previewUrl,
                filePath: claimResult.filePath,
              };
              
              // Clear pending file from localStorage since it's claimed
              clearPendingFile();
            }
          } catch (claimError) {
            console.error("Claim error:", claimError);
            // If claim fails with "claimed by another user", show error
            if (claimError.message?.includes("another user")) {
              setError("This file belongs to another user.");
              setIsLoading(false);
              hasLoadedRef.current = false; // Allow retry
              return;
            }
            // For other errors, try the normal flow
          }
        }

        // If we don't have file data yet, load it the normal way (for non-authenticated users)
        if (!fileData) {
          fileData = await loadPendingFileForConverter(fileId);
          imageData = fileData.imageData;
        }

        setPendingFileData(fileData);
        setFileName(fileData.fileName || "uploaded-image");

        setLoadingMessage("🎨 Detecting colors with AI...");

        // Process the image for the converter (includes AI color detection)
        const result = await processAndAnalyzeImage(imageData);

        // Run AI analysis to determine if retrace is needed
        setLoadingMessage("🤖 Analyzing image for best conversion...");
        let analysisResult = null;
        try {
          analysisResult = await analyzeImageForRetrace(imageData, fileId);
          console.log("🤖 AI Analysis:", analysisResult);
          setAiAnalysisResult(analysisResult);
        } catch (analysisError) {
          console.warn("AI analysis failed, proceeding without:", analysisError);
          // Don't block on analysis failure
        }

        // Set the image data in state
        setImage64(imageData);
        // Set imageSource based on file source (generated vs uploaded)
        const imageSourceValue = fileData.source === "generated" ? "generated" : "uploaded";
        setImageSource(imageSourceValue);

        // Store processed data for converter using context (not window global)
        const processedImage = {
          ...result,
          file: imageData,
        };
        setGuestProcessedImage(processedImage);

        // Save state to sessionStorage for recovery after OAuth sign-in
        saveConverterState({
          pendingFileId: fileId,
          pendingFileData: fileData,
          image64: imageData,
          fileName: fileData.fileName || "uploaded-image",
          processedImage,
        });

        // Track which file ID we just loaded
        lastLoadedFileIdRef.current = fileId;

        setIsLoading(false);
        setConverterOpen(true);

      } catch (error) {
        console.error("Error loading pending file:", error);
        setError(error.message || "Failed to load your image. Please try again.");
        setIsLoading(false);
        hasLoadedRef.current = false; // Allow retry on error
      }
    };

    loadPendingFile();
  }, [location.search, user, converterOpen, savedState, setGuestProcessedImage]);

  // Redirect to homepage when the converter is closed (e.g. user taps back/X)
  // to avoid showing a blank page
  const hasEverOpened = useRef(false);
  useEffect(() => {
    if (converterOpen) {
      hasEverOpened.current = true;
    } else if (hasEverOpened.current && !isLoading) {
      window.location.href = isEmbroideryTrace ? "https://embroiderytrace.com" : "https://svgtrace.com";
    }
  }, [converterOpen, isLoading]);

  // Determine if paywall should be shown:
  // - Not authenticated: show signup paywall
  // - Authenticated but not premium: show subscription paywall
  const shouldShowPaywall = !isAuthenticated || !isPremium;

  /**
   * Context value for the converter
   */
  const contextValue = useMemo(
    () => ({
      svgStore,
      setSvgStore,
      image64,
      setImage64,
      imagePrompt,
      setImagePrompt,
      imageSource,
      setImageSource,
      imageSEO,
      setImageSEO,
      fileName,
      setFileName,
      // Guest mode flags - requires authentication AND premium subscription
      guestMode: shouldShowPaywall,
      onPaywallTrigger: handlePaywallTrigger,
      pendingEmbroideryMode: isEmbroideryMode,
      setPendingEmbroideryMode: () => {},
    }),
    [
      svgStore,
      image64,
      imagePrompt,
      imageSource,
      imageSEO,
      fileName,
      handlePaywallTrigger,
      shouldShowPaywall,
      isEmbroideryMode,
    ]
  );

  /**
   * Render loading state
   */
  if (isLoading) {
    return (
      <IonPage>
        <IonContent className="guest-converter-loading">
          <div className="loading-container">
            <IonSpinner name="crescent" className="loading-spinner" />
            <p className="loading-message">{loadingMessage}</p>
            {pendingFileData && (
              <div className="loading-preview">
                <img
                  src={pendingFileData.imageData}
                  alt="Your upload"
                  className="preview-thumbnail"
                />
              </div>
            )}
          </div>
        </IonContent>
      </IonPage>
    );
  }

  /**
   * Render error state
   */
  if (error) {
    return (
      <IonPage>
        <IonContent className="guest-converter-error">
          <div className="error-container">
            <div className="error-icon">⚠️</div>
            <h2>Oops!</h2>
            <p>{error}</p>
            <button
              className="error-button"
              onClick={() => window.location.href = isEmbroideryTrace ? "https://embroiderytrace.com" : "https://svgtrace.com"}
            >
              Go to Homepage
            </button>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  /**
   * Render converter
   */
  return (
    <IonPage className="guest-converter-page">
      <IonContent className="guest-converter-content" fullscreen>
        <AppContext.Provider value={contextValue}>
          {/* Converter */}
          {converterOpen && (
            <div className="converter-wrapper">
              <Converter
                loadingConverter={loadingConverter}
                setLoadingConverter={setLoadingConverter}
                converterOpen={converterOpen}
                setConverterOpen={setConverterOpen}
                guestMode={shouldShowPaywall}
                pendingFileId={pendingFileId}
                onPaywallTrigger={handlePaywallTrigger}
                initialProcessedImage={window.guestConverterProcessedImage}
                aiAnalysisResult={aiAnalysisResult}
              />
            </div>
          )}

          {/* Paywall Modal */}
          <PaywallModal
            isVisible={showPaywall}
            onClose={handlePaywallClose}
            onSuccess={handlePaywallSuccess}
            action={paywallAction}
            previewImage={pendingFileData?.imageData}
            pendingFileId={pendingFileId}
            isAuthenticated={isAuthenticated}
            isPremium={isPremium}
          />
        </AppContext.Provider>
      </IonContent>
    </IonPage>
  );
};

export default GuestConverter;
